/* assets/ctr-28-buy-box.js
   <ctr28-buy-box> — EN-28 LP buy box (section ctr-en28-buy-box).
   - Gallery: main stage with prev/next + thumbnails (slides are server-rendered, JS toggles `hidden`).
   - Packs: native radio inputs (keyboard + a11y for free); CSS styles the checked card.
   - Add to cart: POST /cart/add.js { id, quantity, selling_plan } then the same events
     as ctr-variety-packs-offer2 so the Alpine mini cart refreshes and opens.

   CNV-6639: optional Subscribe & Save / One-time toggle (section setting "enable_onetime").
   When present, the element carries data-purchase-mode="subscribe|onetime"; one-time adds the
   pack at the regular price (no selling_plan, no Skio box properties).

   DEV NOTE (CNV-6194): subscription only. If a pack has no selling plan (data-selling-plan="")
   it is added as one-time and a console warning is logged — fix the plan setup, don't ship that. */
(function () {
  'use strict';
  if (customElements.get('ctr28-buy-box')) return;

  class Ctr28BuyBox extends HTMLElement {
    connectedCallback() {
      this.initGallery();
      this.initForm();
    }

    /* ---------------- Gallery ---------------- */
    initGallery() {
      this.slides = Array.from(this.querySelectorAll('[data-ctr28-bb-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-ctr28-thumb]'));
      this.current = 0;
      if (this.slides.length < 2) return;

      const prev = this.querySelector('[data-ctr28-gallery-prev]');
      const next = this.querySelector('[data-ctr28-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.showSlide(this.current - 1));
      if (next) next.addEventListener('click', () => this.showSlide(this.current + 1));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showSlide(parseInt(thumb.dataset.ctr28Thumb, 10)));
      });

      // Basic swipe on the stage (mobile)
      const stage = this.querySelector('.ctr28bb__stage');
      let startX = null;
      stage.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      stage.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.showSlide(this.current + (dx < 0 ? 1 : -1));
        startX = null;
      });
    }

    showSlide(index) {
      const count = this.slides.length;
      const i = ((index % count) + count) % count; // wrap around
      this.slides.forEach((slide, n) => {
        const active = n === i;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      this.thumbs.forEach((thumb, n) => {
        const active = n === i;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      this.current = i;
    }

    /* ---------------- Packs + add to cart ---------------- */
    initForm() {
      this.form = this.querySelector('[data-ctr28-form]');
      if (!this.form) return;
      this.submit = this.form.querySelector('[data-ctr28-submit]');
      this.submitLabel = this.form.querySelector('[data-ctr28-submit-label]');
      this.error = this.form.querySelector('[data-ctr28-error]');

      // Make sure something is selected (e.g. default pack sold out).
      if (!this.selectedInput()) {
        const firstAvailable = this.form.querySelector('.ctr28bb__pack-input:not(:disabled)');
        if (firstAvailable) firstAvailable.checked = true;
      }
      this.form.addEventListener('change', (e) => {
        if (this.error) this.error.hidden = true; // new selection clears the previous error
        if (e.target && e.target.matches('[data-ctr28-mode-input]')) this.dataset.purchaseMode = e.target.value;
        this.syncState();
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
      this.syncState();
    }

    // 'subscribe' (default) or 'onetime' — only when the section renders the toggle.
    purchaseMode() {
      const mode = this.form && this.form.querySelector('[data-ctr28-mode-input]:checked');
      return mode ? mode.value : 'subscribe';
    }

    selectedInput() {
      return this.form.querySelector('.ctr28bb__pack-input:checked');
    }

    syncState() {
      const input = this.selectedInput();
      if (this.submit && !this.isAdding) this.submit.disabled = !input || input.disabled;
    }

    setAdding(adding) {
      this.isAdding = adding;
      if (!this.submit) return;
      this.submit.disabled = adding;
      this.submit.setAttribute('aria-busy', adding ? 'true' : 'false');
      if (this.submitLabel) {
        this.submitLabel.textContent = adding ? this.submit.dataset.addingLabel : this.submit.dataset.defaultLabel;
      }
    }

    async addToCart() {
      const input = this.selectedInput();
      if (!input || input.disabled || this.isAdding) return;

      const item = {
        id: parseInt(input.dataset.variantId, 10),
        quantity: parseInt(input.dataset.quantity, 10) || 1,
      };
      const isOneTime = this.purchaseMode() === 'onetime';
      if (isOneTime) {
        // CNV-6639: plain one-time purchase — no selling plan, no Skio box below.
      } else if (input.dataset.sellingPlan) {
        item.selling_plan = parseInt(input.dataset.sellingPlan, 10);
      } else {
        console.warn('[ctr28-buy-box] No selling plan for this pack — adding as one-time purchase. Check Skio plan setup.');
      }

      this.setAdding(true);
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const properties = {};

        // Hide the cart drawer's Subscribe & Save toggle for this line (read in alp-1-items.liquid).
        if (this.hasAttribute('data-hide-subscribe-upsell')) properties._hideSubscribeUpsell = 'true';

        // Skio Dynamic Box: tag the line so Skio applies the box tier discount.
        // _dynamicBoxIndex = 0 for the first box with this id; a second box of the same id gets 1, etc.
        // One-time skips the box unless the section enables "Apply Skio box discount to One-time" (data-onetime-box).
        const boxId = isOneTime && !this.hasAttribute('data-onetime-box') ? '' : this.dataset.boxId;
        if (boxId) {
          let index = 0;
          try {
            const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
            const used = (cart.items || [])
              .filter((line) => line.properties && line.properties._dynamicBoxId === boxId)
              .map((line) => Number(line.properties._dynamicBoxIndex) || 0);
            if (used.length) index = Math.max.apply(null, used) + 1;
          } catch (e) { /* cart read failed → index 0 */ }
          properties._dynamicBoxId = boxId;
          properties._dynamicBoxIndex = index;
        }
        if (Object.keys(properties).length) item.properties = properties;

        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.description || body.message || 'Could not add to cart (' + res.status + ')');
        }

        // Same events as ctr-variety-packs-offer2 → Alpine mini cart refreshes + opens.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:open', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:item-added', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:toast', {
          detail: { type: 'success', message: (input.dataset.title || 'Item') + ' added to cart' },
        }));
        if (window.theme && window.theme.cart && typeof window.theme.cart.getCart === 'function') {
          window.theme.cart.getCart();
        }
      } catch (err) {
        console.error('[ctr28-buy-box] add to cart failed:', err);
        if (this.error) {
          this.error.textContent = err && err.message ? err.message : 'Something went wrong. Please try again.';
          this.error.hidden = false;
        }
        document.dispatchEvent(new CustomEvent('cart:toast', {
          detail: { type: 'error', message: err && err.message ? err.message : 'Could not add this to your cart.' },
        }));
      } finally {
        this.setAdding(false);
        this.syncState();
      }
    }
  }

  customElements.define('ctr28-buy-box', Ctr28BuyBox);
})();
